import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { BillingService } from '../services';
import type { CreateCreditAlertRequest } from '../types';

/**
 * Hook para gerenciar alertas de crédito
 */
export const useAlerts = () => {
  const queryClient = useQueryClient();

  // Get all alerts
  const {
    data: alerts,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['credit-alerts'],
    queryFn: ({ signal }) => BillingService.getAlerts(signal),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Create alert
  const createMutation = useMutation({
    mutationFn: (data: CreateCreditAlertRequest) => BillingService.createAlert(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-alerts'] });
      toast.success('Alerta criado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao criar alerta');
    },
  });

  // Update alert
  const updateMutation = useMutation({
    mutationFn: ({ index, data }: { index: number; data: Partial<CreateCreditAlertRequest> }) =>
      BillingService.updateAlert(index, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-alerts'] });
      toast.success('Alerta atualizado com sucesso');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao atualizar alerta');
    },
  });

  // Delete alert
  const deleteMutation = useMutation({
    mutationFn: (index: number) => BillingService.deleteAlert(index),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-alerts'] });
      toast.success('Alerta removido com sucesso');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao remover alerta');
    },
  });

  return {
    alerts,
    isLoading,
    error,
    refetch,
    createAlert: createMutation.mutate,
    isCreating: createMutation.isPending,
    updateAlert: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    deleteAlert: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};
